import React, { useState, useEffect } from 'react';
import { getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../Messages';
import { showApiError } from '../../utils/errorUtils';
import { useTranslation } from 'react-i18next';

const ReassignTaskModal = ({ isOpen, onClose, task, onReassignComplete }) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const { showSuccess, showError } = useMessages();
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      setSelectedUserId('');
      setNotes('');
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    setUsersError(null);
    try {
      const response = await apiFetch(getApiUrl('/api/users/'));
      if (response.ok) {
        const data = await response.json();
        const active = (data || []).filter(u => u.is_active);
        setUsers(active);
      } else {
        setUsers([]);
        setUsersError(tCommon('errors.usersLoadFailed'));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setUsersError(tCommon('errors.usersLoadFailed'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      showError(t('reassignTaskModal.toasts.noOperatorTitle'), t('reassignTaskModal.toasts.noOperatorMessage'));
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/production-tasks/assignments'), {
        method: 'POST',
        body: JSON.stringify({
          task_id: task.task_id,
          assigned_to: parseInt(selectedUserId),
          assigned_by: user.user_id,
          is_primary: true,
          assignment_notes: notes || null
        })
      });

      if (response.ok) {
        showSuccess(
          t('reassignTaskModal.toasts.reassignedTitle'),
          t('reassignTaskModal.toasts.reassignedMessage', { code: task.task_code })
        );
        onClose();
        onReassignComplete();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error reassigning task:', error);
      showError(tCommon('ui.networkError'), t('reassignTaskModal.toasts.reassignFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  if (!isOpen || !task) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{t('reassignTaskModal.title')}</h2>
          <button className="dialog-close" onClick={handleClose} disabled={loading}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dialog-body">
            <div className="task-info-section">
              <h3>{t('reassignTaskModal.taskInformation')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('reassignTaskModal.taskCode')}:</label>
                  <span>{task.task_code}</span>
                </div>
                <div className="info-item">
                  <label>{t('reassignTaskModal.taskName')}:</label>
                  <span>{task.task_name}</span>
                </div>
                {task.po_number && (
                  <div className="info-item">
                    <label>{t('reassignTaskModal.productionOrder')}:</label>
                    <span>{task.po_number}</span>
                  </div>
                )}
                {task.last_operator_name && (
                  <div className="info-item">
                    <label>{t('reassignTaskModal.lastOperator')}:</label>
                    <span>{task.last_operator_name.trim() || task.last_operator_username}</span>
                  </div>
                )}
                {task.last_shift_quantity != null && (
                  <div className="info-item">
                    <label>{t('reassignTaskModal.lastShiftQty')}:</label>
                    <span>{task.last_shift_quantity} {task.last_shift_uom}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="approval-section">
              <h3>{t('reassignTaskModal.assignNextOperator')}</h3>
              <div className="form-group">
                <label>{t('reassignTaskModal.operator')} *</label>
                <select
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="">{t('reassignTaskModal.selectOperator')}</option>
                  {users.map(u => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.first_name} {u.last_name} ({u.username}) — {u.role}
                    </option>
                  ))}
                </select>
                {usersError && <div className="error-message" role="alert" style={{ padding: "0.5rem 0", fontSize: "0.9rem" }}>{usersError}</div>}
              </div>

              <div className="form-group">
                <label>{t('reassignTaskModal.assignmentNotes')}</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder={t('reassignTaskModal.notesPlaceholder')}
                  rows="3"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="dialog-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              {tCommon('ui.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !selectedUserId}
            >
              {loading ? t('reassignTaskModal.assigning') : t('reassignTaskModal.assignOperator')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReassignTaskModal;
